import { safeApiCall } from "api/common.service";
import { getOverdueAccount } from "api/debtor.service";
import { getSeverity } from "common/utils";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { OverdueDebtors } from "types/model.types";

const OverdueAccountPreviews: React.FC = () => {
  const [data, setData] = useState<OverdueDebtors>([]);
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const searchTerm = params.get("s") ?? "";
  const statusFilter = params.get("f") ?? "";

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const result = await safeApiCall(getOverdueAccount(false));
        if (mounted && result) setData(result);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchData();

    return () => {
      mounted = false;
    };
  }, [searchTerm]);

  const filteredItems = useMemo(() => {
    const normalizedSearch = (searchTerm ?? "").trim().toLowerCase();
    if (!data) return [];

    return data.filter((b) => {
      const name = b.name?.toLowerCase() ?? "";
      const alpha = b.alpha?.toLowerCase() ?? "";

      let matchesSearch = true;
      if (normalizedSearch.length >= 3) {
        matchesSearch =
          name.includes(normalizedSearch) ||
          alpha.includes(normalizedSearch) ||
          name.startsWith(normalizedSearch) ||
          alpha.startsWith(normalizedSearch);
      }
      let matchesStatus = true;

      if (statusFilter) {
        const severity = getSeverity(b);
        matchesStatus =
          statusFilter === null || severity.toString() === statusFilter;
      }

      return matchesSearch && matchesStatus;
    });
    //.sort((a, b) => getSeverity(b) - getSeverity(a)); // Descending
  }, [data, searchTerm]);

  const totals = useMemo(() => {
    return {
      opening: filteredItems.reduce((s, d) => s + d.opening, 0),
      balance: filteredItems.reduce((s, d) => s + d.balance, 0),
      current: filteredItems.reduce((s, d) => s + d.currentMonth, 0),
      month1: filteredItems.reduce((s, d) => s + d.month1, 0),
      month2: filteredItems.reduce((s, d) => s + d.month2, 0),
      month3: filteredItems.reduce((s, d) => s + d.month3, 0),
    };
  }, [filteredItems]);

  const rows = filteredItems.map((debtor) => {
    let overdueLabel = "";

    if (debtor.month3 > 0) {
      overdueLabel = "3+ Months";
    } else if (debtor.month2 > 0) {
      overdueLabel = "2 Months";
    } else {
      overdueLabel = "-";
    }

    return (
      <tr key={debtor.id}>
        <td>{debtor.id}</td>
        <td className="description xs">{debtor.name}</td>
        <td className="money">{debtor.opening.toFixed(2)}</td>
        <td className="money">{debtor.balance.toFixed(2)}</td>
        <td className="money">{debtor.currentMonth.toFixed(2)}</td>
        <td className="money">{debtor.month1.toFixed(2)}</td>
        <td className="money">{debtor.month2.toFixed(2)}</td>
        <td className="money">{debtor.month3.toFixed(2)}</td>
        <td>{overdueLabel}</td>
      </tr>
    );
  });

  return (
    <>
      <div className="sub-title">Overdue Accounts</div>
      <table className="details-table">
        <thead>
          <tr>
            <th>ID</th>
            <th className="description xs">Name</th>
            <th className="money">Opening</th>
            <th className="money">Balance</th>
            <th className="money">Current</th>
            <th className="money">Month 1</th>
            <th className="money">Month 2</th>
            <th className="money">Month 3</th>
            <th>Overdue</th>
          </tr>
        </thead>
        <tbody>
          {rows}
          <tr className="totals-row">
            <td></td>
            <td className="alignr">Grand Totals</td>
            <td className="money money-focal">{totals.opening.toFixed(2)}</td>
            <td className="money money-focal">{totals.balance.toFixed(2)}</td>
            <td className="money money-focal">{totals.current.toFixed(2)}</td>
            <td className="money money-focal">{totals.month1.toFixed(2)}</td>
            <td className="money money-focal">{totals.month2.toFixed(2)}</td>
            <td className="money money-focal">{totals.month3.toFixed(2)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default OverdueAccountPreviews;
